import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';
import api from '../api';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export function CartProvider({ children }) {
  const { userId: authUserId, authReady } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);
  const pendingSyncRef = useRef({
    clearCart: false,
    quantities: {}
  });

  const queueQuantitySync = useCallback((id, quantity) => {
    const pending = pendingSyncRef.current;

    if (quantity > 0) {
      pending.quantities[id] = quantity;
      return;
    }

    delete pending.quantities[id];
  }, []);

  const queueClearCartSync = useCallback(() => {
    pendingSyncRef.current = {
      clearCart: true,
      quantities: {}
    };
  }, []);

  const persistItemQuantity = useCallback(async (userId, itemId, quantity) => {
    try {
      await api.delete('/carrinho', {
        data: { id_usuario: userId, id_variacao: itemId },
        withCredentials: true
      });
    } catch (error) {
      if (error?.response?.status !== 404) {
        throw error;
      }
    }

    if (quantity > 0) {
      await api.post(
        '/carrinho',
        {
          id_usuario: userId,
          id_variacao: itemId,
          quantidade: quantity
        },
        { withCredentials: true }
      );
    }
  }, []);

  const fetchCartFromServer = useCallback(async userId => {
    // Sempre envia o id do usuário para garantir o preço correto
    const res = await api.get(`/carrinho/${userId}`, {
      params: { usuario_id: userId },
      withCredentials: true
    });

    if (res.data.success) {
      const raw = res.data.data.map(item => ({
        id: item.id_variacao,
        id_produto: item.id_produto,
        quantidade: Number(item.quantidade) || 0,
        nome: item.nome,
        preco: Number(item.preco) || 0,
        imagem_principal: item.imagem_principal || item.url || '',
        cor: item.cor || '',
        tamanho: item.tamanho || '',
        tipo: item.tipo || '',
        material: item.material || '',
        estoque: Number(item.estoque) || 0,
        unidades_por_pacote: item.unidades_por_pacote || 1
      }));

      setItems(raw);
    }
  }, []);

  const flushPendingSync = useCallback(async userId => {
    const pending = pendingSyncRef.current;
    const entries = Object.entries(pending.quantities);

    if (!pending.clearCart && entries.length === 0) {
      return;
    }

    pendingSyncRef.current = {
      clearCart: false,
      quantities: {}
    };

    if (pending.clearCart) {
      await api.delete('/limparcarrinho', {
        data: { id_usuario: userId },
        withCredentials: true
      });
    }

    for (const [itemId, quantity] of entries) {
      await persistItemQuantity(userId, itemId, quantity);
    }
  }, [persistItemQuantity]);

  // Sempre que trocar o usuário autenticado, libera novo fetch e evita vazar carrinho entre contas.
  useEffect(() => {
    fetchedRef.current = false;

    if (!authReady) {
      setLoading(true);
      return;
    }

    if (!authUserId) {
      pendingSyncRef.current = {
        clearCart: false,
        quantities: {}
      };
      setItems([]);
      setLoading(false);
    }
  }, [authReady, authUserId]);

  // Busca carrinho no BD assim que userId estiver definido
  useEffect(() => {
    if (!authReady || authUserId === undefined || authUserId === null || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);

    (async () => {
      try {
        await flushPendingSync(authUserId);
        await fetchCartFromServer(authUserId);
      } catch (err) {
        console.error('Erro ao buscar carrinho:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [authReady, authUserId, fetchCartFromServer, flushPendingSync]);

  // 1) removeItem DEVE vir antes de updateQuantity
  const removeItem = useCallback(
    id => {
      setItems(curr => curr.filter(i => i.id !== id));
      if (!authReady || !authUserId) {
        queueQuantitySync(id, 0);
        return;
      }

      persistItemQuantity(authUserId, id, 0).catch(e =>
        console.error('Erro ao remover item no servidor:', e)
      );
    },
    [authReady, authUserId, persistItemQuantity, queueQuantitySync]
  );

  const updateQuantity = useCallback(
    (id, delta) => {
      console.log('[CartContext] updateQuantity chamado com:', { id, delta });
      setItems(curr => {
        const item = curr.find(i => i.id === id);
        if (!item) return curr;

        const novaQtd = item.quantidade + delta;
        if (novaQtd < 1) {
          removeItem(id);
          return curr.filter(i => i.id !== id);
        }

        const updated = curr.map(i =>
          i.id === id ? { ...i, quantidade: novaQtd } : i
        );

        if (!authReady || !authUserId) {
          queueQuantitySync(id, novaQtd);
          return updated;
        }

        persistItemQuantity(authUserId, id, novaQtd).catch(e =>
          console.error('Erro ao ajustar quantidade no servidor:', e)
        );

        return updated;
      });
    },
    [authReady, authUserId, persistItemQuantity, queueQuantitySync, removeItem]
  );

  const addToCart = useCallback(
    prod => {
      setItems(curr => {
        const exists = curr.find(i => i.id === prod.id);
        const updated = exists
          ? curr.map(i =>
            i.id === prod.id
              ? { ...i, quantidade: i.quantidade + 1 }
              : i
          )
          : [
            ...curr,
            {
              id: prod.id,
              id_produto: prod.id_produto,
              nome: prod.nome,
              preco: Number(prod.preco) || 0,
              imagem_principal: prod.imagem_principal,
              cor: prod.cor || '',
              tamanho: prod.tamanho || '',
              tipo: prod.tipo || '',
              material: prod.material || '',
              estoque: Number(prod.estoque) || 0,
              unidades_por_pacote: prod.unidades_por_pacote || 1,
              quantidade: 1
            }
          ];

        const novaQtd = exists ? exists.quantidade + 1 : 1;

        if (!authReady || !authUserId) {
          queueQuantitySync(prod.id, novaQtd);
          return updated;
        }

        persistItemQuantity(authUserId, prod.id, novaQtd).catch(e =>
          console.error('Erro ao salvar carrinho:', e)
        );

        return updated;
      });
    },
    [authReady, authUserId, persistItemQuantity, queueQuantitySync]
  );

  const clearCart = useCallback(() => {
    setItems([]);
    if (!authReady || !authUserId) {
      queueClearCartSync();
      return;
    }

    api
      .delete('/limparcarrinho', {
        data: { id_usuario: authUserId },
        withCredentials: true
      })
      .catch(e => console.error('Erro ao limpar carrinho:', e));
  }, [authReady, authUserId, queueClearCartSync]);

  const resetCart = useCallback(() => {
    setItems([]);
  }, []);

  const atualizarCarrinho = useCallback(() => {
    if (!authReady || authUserId === undefined || authUserId === null) return;

    setLoading(true);
    flushPendingSync(authUserId)
      .then(() => fetchCartFromServer(authUserId))
      .catch(err => console.error('Erro ao buscar carrinho:', err))
      .finally(() => setLoading(false));
  }, [authReady, authUserId, fetchCartFromServer, flushPendingSync]);

  return (
    <CartContext.Provider
      value={{
        items,
        loading,
        addToCart,
        updateQuantity,
        removeItem,
        clearCart,
        resetCart,
        atualizarCarrinho
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}